/**
 * The small form pieces the inspector panels share: a text field and a text
 * area that commit once (on blur, on Enter, or on Ctrl/Cmd+Enter in the text
 * area) instead of on every keystroke, an "add" field that clears itself, an
 * icon button, the colour chips, section and field wrappers, and the options
 * of a node picker.
 *
 * Committing once per finished edit is what keeps one change = one undo step
 * = one save, which the hosts rely on (FlowMapProps.onChange). Escape puts a
 * field back to the stored value. Every visible word comes from FlowMapLabels.
 */
import { useEffect, useId, useRef, useState, type ChangeEvent, type ComponentType, type KeyboardEvent, type ReactElement, type ReactNode } from "react";
import { nodeTitle } from "./canvasModel.js";
import type { FlowLinkFieldProps } from "./props.js";
import type { FlowRefChoices } from "./types.js";
import { IconPlus } from "./icons.js";
import { FLOW_TONE_LABEL } from "./labels.js";
import { FLOW_TONES, updateNode, type FlowMapDoc, type FlowMapLabels, type FlowNode, type FlowTone, type NodePatch } from "./model.js";

/** Apply an edit to the LATEST document; true when it changed something (and was committed). */
export type FlowEdit = (fn: (doc: FlowMapDoc) => FlowMapDoc) => boolean;

/** What every editor panel works with. */
export interface EditorEnv {
  doc: FlowMapDoc;
  labels: FlowMapLabels;
  /** The language the fields show (the one they write when `textLocale` is set). */
  locale?: string;
  /** Localised writes, see FlowMapProps.textLocale. Unset: plain strings. */
  textLocale?: string;
  /** The language an untagged text is already in, see FlowMapProps.textDefaultLocale. */
  textDefaultLocale?: string;
  /** The host's address field, see FlowMapProps.LinkField. Unset: a plain text box. */
  LinkField?: ComponentType<FlowLinkFieldProps>;
  /** A pointer offered as a choice, see FlowMapProps.refChoices. Unset: no such field. */
  refChoices?: FlowRefChoices;
  edit: FlowEdit;
}

export function Section({ title, aside, children }: { title: string; aside?: ReactNode; children: ReactNode }): ReactElement {
  return (
    <section className="wa-fm__section">
      <h4 className="wa-fm__section-title">
        <span>{title}</span>
        {aside}
      </h4>
      {children}
    </section>
  );
}

export function Field({ label, htmlFor, children }: { label: string; htmlFor: string; children: ReactNode }): ReactElement {
  return (
    <div className="wa-fm__field">
      <label className="wa-fm__label" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
    </div>
  );
}

export function IconButton({
  label,
  onClick,
  disabled,
  danger,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  children: ReactNode;
}): ReactElement {
  return (
    <button
      type="button"
      className={`wa-fm__btn wa-fm__btn--icon wa-fm__btn--sm${danger ? " wa-fm__btn--danger" : ""}`}
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

export interface CommitTextProps {
  value: string;
  onCommit: (value: string) => void;
  id?: string;
  /** Accessible name when the field has no visible <label>. */
  ariaLabel?: string;
  placeholder?: string;
  multiline?: boolean;
  maxLength?: number;
  /** Focus the field when it first appears (a node that was just added). */
  autoFocus?: boolean;
  invalid?: boolean;
  describedBy?: string;
  /** Every keystroke, e.g. to clear an error while the author is still typing. */
  onDraft?: (value: string) => void;
}

/** A text field that keeps a local draft and hands it over once, when the edit is finished. */
export function CommitText(p: CommitTextProps): ReactElement {
  const [draft, setDraft] = useState(p.value);
  const focused = useRef(false);
  const el = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const latest = useRef({ draft, value: p.value, onCommit: p.onCommit });
  latest.current = { draft, value: p.value, onCommit: p.onCommit };

  // Follow outside changes (undo, an edit on the canvas) while nobody types here.
  useEffect(() => {
    if (!focused.current) setDraft(p.value);
  }, [p.value]);

  // A field that disappears mid-edit (the selection moved on) still keeps what was typed.
  useEffect(
    () => () => {
      const l = latest.current;
      if (focused.current && l.draft !== l.value) l.onCommit(l.draft);
    },
    [],
  );

  const autoFocus = p.autoFocus;
  useEffect(() => {
    // preventScroll: on a narrow map the panel sits under the canvas; focusing must not scroll the canvas away.
    if (autoFocus) el.current?.focus({ preventScroll: true });
  }, [autoFocus]);

  const commit = (): void => {
    if (draft !== p.value) p.onCommit(draft);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    if (e.key === "Escape") {
      if (draft !== p.value) {
        e.preventDefault();
        setDraft(p.value);
      }
      return;
    }
    if (e.key === "Enter" && (!p.multiline || e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      commit();
    }
  };
  const common = {
    id: p.id,
    value: draft,
    placeholder: p.placeholder,
    maxLength: p.maxLength,
    "aria-label": p.ariaLabel,
    "aria-invalid": p.invalid ? true : undefined,
    "aria-describedby": p.describedBy,
    onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
      setDraft(e.target.value);
      p.onDraft?.(e.target.value);
    },
    onFocus: (): void => {
      focused.current = true;
    },
    onBlur: (): void => {
      focused.current = false;
      commit();
    },
    onKeyDown,
  };
  return p.multiline ? (
    <textarea
      ref={(node) => {
        el.current = node;
      }}
      className="wa-fm__textarea"
      rows={3}
      {...common}
    />
  ) : (
    <input
      ref={(node) => {
        el.current = node;
      }}
      type="text"
      className="wa-fm__input"
      {...common}
    />
  );
}

/** A field plus a button that add one entry (Enter works too) and then clear the field. */
export function AddText({
  label,
  placeholder,
  maxLength,
  onAdd,
}: {
  label: string;
  placeholder?: string;
  maxLength?: number;
  onAdd: (text: string) => void;
}): ReactElement {
  const [value, setValue] = useState("");
  const text = value.trim();
  const add = (): void => {
    if (!text) return;
    onAdd(text);
    setValue("");
  };
  return (
    <div className="wa-fm__row">
      <input
        type="text"
        className="wa-fm__input"
        value={value}
        placeholder={placeholder}
        aria-label={label}
        maxLength={maxLength}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            add();
          } else if (e.key === "Escape" && value) {
            e.preventDefault();
            setValue("");
          }
        }}
      />
      <IconButton label={label} onClick={add} disabled={!text}>
        <IconPlus size={14} />
      </IconButton>
    </div>
  );
}

/** The node's colour as a radio group of dots; the names are for screen readers and the hover title. */
export function ToneChips({
  value,
  labels,
  onChange,
}: {
  value: FlowTone | undefined;
  labels: FlowMapLabels;
  onChange: (tone: FlowTone) => void;
}): ReactElement {
  const name = useId();
  const headId = `${name}-tone`;
  return (
    <div className="wa-fm__field">
      <span className="wa-fm__label" id={headId}>
        {labels.tone}
      </span>
      <div className="wa-fm__tones" role="radiogroup" aria-labelledby={headId}>
        {FLOW_TONES.map((t) => {
          const text = labels[FLOW_TONE_LABEL[t]];
          return (
            <label key={t} className={`wa-fm__tone wa-fm__tone--${t}`} title={text}>
              <input type="radio" className="wa-fm__sr" name={name} value={t} checked={value === t} onChange={() => onChange(t)} />
              <span className="wa-fm__tone-dot" aria-hidden="true" />
              <span className="wa-fm__sr">{text}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

/** Every node (but `exclude`) as a picker option, titled the way the map shows it. */
export function nodeOptions(
  doc: FlowMapDoc,
  locale: string | undefined,
  labels: FlowMapLabels,
  exclude?: string,
): Array<{ id: string; title: string }> {
  return doc.nodes.filter((n) => n.id !== exclude).map((n) => ({ id: n.id, title: nodeTitle(n, locale, labels) }));
}

/** Patch the node as it is in the latest document; `null` from `fn` means "no change". */
export type Patch = (fn: (node: FlowNode) => NodePatch | null) => void;

export function patcher(env: EditorEnv, id: string): Patch {
  return (fn) => {
    env.edit((d) => {
      const n = d.nodes.find((x) => x.id === id);
      if (!n) return d;
      const patch = fn(n);
      return patch ? updateNode(d, id, patch) : d;
    });
  };
}
